import { useEffect, useId, useRef, useState } from "react";
import { CONFIG_COLORS, paletteColor } from "../lib/palette";

/**
 * Escolha de cor de um item de configuração — etapa, tag ou gatilho.
 *
 * É a "bolinha de cor" do §18.1: um popover NÃO modal com a paleta do produto,
 * nunca o seletor nativo do navegador. Fecha por Escape e por clique fora, e
 * devolve o foco ao gatilho. As setas andam pela grade, como um grupo de rádio.
 */

export interface ColorMenuProps {
  /** Item que recebe a cor; entra no nome acessível. */
  about: string;
  value?: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  /** Motivo visível da indisponibilidade (§18). */
  disabledReason?: string;
}

export function ColorMenu({ about, value, onChange, disabled, disabledReason }: ColorMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const atual = CONFIG_COLORS.find((swatch) => swatch.value.toLowerCase() === value?.toLowerCase());
  const name = `Cor de ${about}${atual ? `: ${atual.name}` : ""}`;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const escolhe = (color: string) => {
    onChange(color);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const anda = (index: number, delta: number) => {
    const alvo = (index + delta + CONFIG_COLORS.length) % CONFIG_COLORS.length;
    const node = rootRef.current?.querySelectorAll<HTMLButtonElement>("[data-swatch]")[alvo];
    node?.focus();
  };

  return (
    <span ref={rootRef} className="cx-colormenu">
      <button
        ref={triggerRef}
        type="button"
        className="cx-colordot"
        aria-label={disabled && disabledReason ? `${name} — ${disabledReason}` : name}
        title={disabled && disabledReason ? disabledReason : name}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        disabled={disabled}
        style={value ? { background: paletteColor(value) } : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      />
      {open && (
        <div id={panelId} className="cx-colorpop" role="group" aria-label={`Cores de ${about}`}>
          {CONFIG_COLORS.map((swatch, index) => (
            <button
              key={swatch.value}
              type="button"
              data-swatch=""
              aria-label={swatch.name}
              aria-pressed={swatch.value.toLowerCase() === value?.toLowerCase()}
              style={{ background: paletteColor(swatch.value) }}
              onClick={() => escolhe(swatch.value)}
              onKeyDown={(event) => {
                if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                  event.preventDefault();
                  anda(index, 1);
                }
                if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                  event.preventDefault();
                  anda(index, -1);
                }
              }}
            />
          ))}
        </div>
      )}
    </span>
  );
}
